import {
  Directive,
  DOCUMENT,
  effect,
  EffectCleanupRegisterFn,
  ElementRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';

@Directive({
  selector: '[upuScrollObserver]',
  exportAs: 'scrollObserver',
})
export class ScrollObserver {
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly hasScrolled = model<boolean>();
  // CSS selector of the target element to observe
  readonly targetSelector = input<'root' | (string & {})>();
  // Sentinel element ID to observe
  readonly scrollSentinelId = input.required<string>();
  // Intersection observer threshold to trigger scroll detection
  readonly observerThreshold = input<number>(1);
  // Intersection observer root margin
  readonly observerRootMargin = input<string>('0px');
  protected readonly scrollSentinel = this.document.createElement('div');

  protected readonly rootElement = signal<HTMLElement | null>(null);
  private addedRelative = false;

  constructor() {
    effect((onCleanup) => this.startObserver(onCleanup));
  }

  private startObserver(onCleanup: EffectCleanupRegisterFn): void {
    const selector = this.targetSelector();
    const root =
      selector && selector !== 'root'
        ? this.document.querySelector<HTMLElement>(selector)
        : selector === 'root'
          ? this.document.documentElement
          : this.elementRef.nativeElement;

    this.rootElement.set(root);

    if (!root || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const threshold = this.observerThreshold();
    const rootMargin = this.observerRootMargin();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.hasScrolled.set(!entry.isIntersecting);
        });
      },
      {
        root: selector === 'root' ? null : root,
        threshold,
        rootMargin,
      },
    );

    this.appendScrollSentinel();
    observer.observe(this.scrollSentinel);

    onCleanup(() => {
      observer.disconnect();
      this.removeScrollSentinel();
    });
  }

  private appendScrollSentinel(): void {
    const root = this.rootElement();

    if (!root) {
      return;
    }

    this.scrollSentinel.setAttribute(
      'data-sentinel-id',
      this.scrollSentinelId(),
    );

    this.scrollSentinel.style.position = 'absolute';
    this.scrollSentinel.style.top = '0';
    this.scrollSentinel.style.left = '0';
    this.scrollSentinel.style.width = '1px';
    this.scrollSentinel.style.height = '1px';
    this.scrollSentinel.style.pointerEvents = 'none';

    if (!root.classList.contains('relative')) {
      root.classList.add('relative');
      this.addedRelative = true;
    } else {
      this.addedRelative = false;
    }

    root.prepend(this.scrollSentinel);
  }

  private removeScrollSentinel(): void {
    if (this.addedRelative) {
      this.rootElement()?.classList.remove('relative');
    }

    this.scrollSentinel.remove();
    this.addedRelative = false;
  }
}
